import { Controller } from './controller';
import type { PhysicsEngine } from './engine';
import { netForce, scheduleValue } from './model';
import { SeededNormal } from './noise';
import type { ExperimentConfig, ManualEvent, RunStats, RunStatus, Sample, StopReason } from './types';

/** Record one sample every this many physics steps (0.025 s at Δt = 0.005 s). */
export const RECORD_EVERY = 5;
/** Altitude above which the vehicle leaves the teaching guide. */
export const GUIDE_TOP = 12;

export interface ValidationIssue {
  field: string;
  message: string;
}

export function validateConfig(c: ExperimentConfig): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const finite = (x: number, field: string) => {
    if (!Number.isFinite(x)) issues.push({ field, message: `${field} must be a finite number.` });
  };
  const p = c.plant;
  finite(p.m, 'mass');
  finite(p.g, 'gravity');
  finite(p.k, 'spring stiffness');
  finite(p.b, 'damping');
  finite(p.zs, 'spring rest altitude');
  finite(p.Tmax, 'maximum thrust');
  if (p.m <= 0) issues.push({ field: 'mass', message: 'Mass must be positive.' });
  if (p.g < 0) issues.push({ field: 'gravity', message: 'g is a magnitude and cannot be negative.' });
  if (p.k < 0) issues.push({ field: 'spring stiffness', message: 'Spring stiffness cannot be negative.' });
  if (p.b < 0) issues.push({ field: 'damping', message: 'Physical damping cannot be negative.' });
  if (p.Tmax <= 0) issues.push({ field: 'maximum thrust', message: 'Maximum thrust must be positive.' });
  if (!(c.dt > 0 && c.dt <= 0.02)) issues.push({ field: 'Δt', message: 'Step size must be in (0, 0.02] s.' });
  if (!(c.duration > 0 && c.duration <= 120)) issues.push({ field: 'duration', message: 'Duration must be in (0, 120] s.' });
  if (!(c.initial.z > 0 && c.initial.z < GUIDE_TOP))
    issues.push({ field: 'initial altitude', message: `Start above the ground and below ${GUIDE_TOP} m.` });
  const k = c.controller;
  for (const [name, v] of [
    ['K_p', k.Kp],
    ['K_i', k.Ki],
    ['K_d', k.Kd],
  ] as const) {
    finite(v, name);
    if (v < 0) issues.push({ field: name, message: `${name} must not be negative in this tutorial.` });
  }
  if (k.derivTau < 0) issues.push({ field: 'filter', message: 'Filter time constant cannot be negative.' });
  if (k.hysteresis < 0) issues.push({ field: 'h', message: 'Hysteresis half-band cannot be negative.' });
  if (c.noise.enabled && !(c.noise.sigma >= 0)) issues.push({ field: 'noise', message: 'Noise level must be ≥ 0.' });
  return issues;
}

/**
 * One experiment run. Deterministic given the config, seed and manual events,
 * so replay reproduces the trajectory within numerical tolerance.
 */
export class Simulation {
  readonly trace: Sample[] = [];
  readonly manualEvents: ManualEvent[] = [];
  stats: RunStats;
  status: RunStatus = 'ready';
  stopReason: StopReason | null = null;
  stepIndex = 0;
  latest: Sample;

  private controller: Controller;
  private noise: SeededNormal;
  private readonly totalSteps: number;
  private replayEvents: ManualEvent[] = [];
  private replayCursor = 0;

  constructor(
    private readonly engine: PhysicsEngine,
    readonly config: ExperimentConfig,
    replayManualEvents: ManualEvent[] = [],
  ) {
    const issues = validateConfig(config);
    if (issues.length) throw new Error(issues.map((i) => i.message).join(' '));
    this.totalSteps = Math.round(config.duration / config.dt);
    this.controller = new Controller(config.controller, config.plant, config.z0, config.dt);
    this.noise = new SeededNormal(config.noise.seed);
    this.stats = this.freshStats();
    this.replayEvents = [...replayManualEvents].sort((a, b) => a.step - b.step);
    this.engine.configure(config.plant, config.dt);
    this.engine.setState(config.initial.z, config.initial.v);
    this.latest = this.computeSample();
    this.trace.push(this.latest);
  }

  get t(): number {
    return this.stepIndex * this.config.dt;
  }

  get done(): boolean {
    return this.status === 'complete' || this.status === 'stopped';
  }

  /**
   * Change the manual input. It takes effect at the next controller update (step index + 1);
   * the event is recorded with that step so replay reproduces the run exactly.
   */
  setManualInput(value: number): void {
    if (!Number.isFinite(value) || this.done) return;
    this.applyManual(this.stepIndex + 1, value);
  }

  private applyManual(step: number, value: number): void {
    this.controller.setManualInput(value);
    const last = this.manualEvents[this.manualEvents.length - 1];
    if (last && last.step === step) last.value = value;
    else this.manualEvents.push({ step, value });
  }

  get manualInput(): number {
    return this.controller.manualInput;
  }

  private freshStats(): RunStats {
    const z = this.config.initial.z;
    return { saturationTime: 0, iae: 0, switchCount: 0, maxAltitude: z, minAltitude: z };
  }

  /**
   * Computes controller output and the sample for the current instant.
   * Must be called exactly once per step (the controller has memory).
   */
  private computeSample(): Sample {
    while (this.replayCursor < this.replayEvents.length && this.replayEvents[this.replayCursor].step <= this.stepIndex) {
      const ev = this.replayEvents[this.replayCursor];
      this.applyManual(ev.step, ev.value);
      this.replayCursor++;
    }
    const c = this.config;
    const t = this.t;
    const z = this.engine.z;
    const v = this.engine.v;
    const r = scheduleValue(c.reference, t);
    const d = scheduleValue(c.disturbance, t);
    const n = c.noise.enabled ? c.noise.sigma * this.noise.next() : 0;
    const ym = z + n;
    const out = this.controller.step({ r, ym });
    if (out.switched) this.stats.switchCount++;
    const a = netForce(c.plant, z, v, out.T, d) / c.plant.m;
    return {
      t,
      z,
      v,
      a,
      r,
      ym,
      e: out.e,
      Tff: out.Tff,
      uP: out.uP,
      uI: out.uI,
      uD: out.uD,
      ufb: out.ufb,
      Tcmd: out.Tcmd,
      T: out.T,
      d,
      I: out.I,
      vEst: out.vEst,
      sat: out.sat,
      sw: out.sw,
    };
  }

  /** Advance one physics step. Returns false if the run is over. */
  stepOnce(): boolean {
    if (this.done) return false;
    this.status = 'running';
    const s = this.latest;
    const dt = this.config.dt;
    if (!Number.isFinite(s.T) || !Number.isFinite(s.d)) {
      return this.stop('invalid-input', 'The thrust or disturbance input became invalid (not a finite number).');
    }
    this.engine.step(s.T, s.d);
    this.stats.iae += Math.abs(s.r - s.z) * dt;
    if (s.sat !== 0) this.stats.saturationTime += dt;
    this.stepIndex++;

    const z = this.engine.z;
    const v = this.engine.v;
    if (!Number.isFinite(z) || !Number.isFinite(v)) {
      return this.stop('nonfinite', 'The simulation produced a non-finite value. Reduce the gains and start a new run.');
    }
    this.stats.maxAltitude = Math.max(this.stats.maxAltitude, z);
    this.stats.minAltitude = Math.min(this.stats.minAltitude, z);
    this.latest = this.computeSample();
    const atEnd = this.stepIndex >= this.totalSteps;
    if (this.stepIndex % RECORD_EVERY === 0 || atEnd) this.trace.push(this.latest);

    if (z <= 0) {
      if (this.trace[this.trace.length - 1] !== this.latest) this.trace.push(this.latest);
      return this.stop(
        'contact',
        'The vehicle crashed into the ground (z = 0).',
      );
    }
    if (z >= GUIDE_TOP) {
      if (this.trace[this.trace.length - 1] !== this.latest) this.trace.push(this.latest);
      return this.stop('ceiling', `The vehicle reached the top of the ${GUIDE_TOP} m guide, so the run stopped.`);
    }
    if (atEnd) {
      this.status = 'complete';
      return false;
    }
    return true;
  }

  /** Advance up to `seconds` of simulation time (bounded by the run end). Returns number of steps taken. */
  advance(seconds: number): number {
    const steps = Math.max(0, Math.round(seconds / this.config.dt));
    let taken = 0;
    for (let i = 0; i < steps; i++) {
      if (this.done) break;
      if (!this.stepOnce()) {
        taken++;
        break;
      }
      taken++;
    }
    return taken;
  }

  runToEnd(): void {
    while (this.stepOnce()) {
      /* keep stepping */
    }
  }

  private stop(code: StopReason['code'], message: string): false {
    this.status = 'stopped';
    this.stopReason = { code, t: this.t, message };
    return false;
  }
}
